// ============================================================================
// EVIDENCE REVIEW - AUDITOR PAGE
// ============================================================================
// Purpose: Review evidence submitted by compliance users
// ============================================================================

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { auditApi, EvidenceReview as EvidenceReviewType } from '../../audit/api';
import { complianceExecutionApi } from '../../compliance/execution/api/complianceExecution.api';
import { RefreshCw, Search, Download, X, ArrowRight } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';
import { useAuth } from '@contexts/AuthContext';

const EvidenceReview: React.FC = () => {
  const { user } = useAuth();
  const [evidence, setEvidence] = useState<EvidenceReviewType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvidence, setSelectedEvidence] = useState<EvidenceReviewType | null>(null);
  const [assessmentNotes, setAssessmentNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  useEffect(() => {
    loadEvidence();
  }, []);

  const loadEvidence = async () => {
    try {
      const evidenceData = await auditApi.getEvidenceForReview();
      setEvidence(evidenceData);
    } catch (error) {
      console.error('Failed to load evidence:', error);
      setEvidence([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'INTERNAL_VERIFIED': return 'text-green-700 bg-green-100';
      case 'approved': return 'text-green-700 bg-green-100';
      case 'submitted': return 'text-amber-700 bg-amber-100';
      case 'pending_review': return 'text-amber-700 bg-amber-100';
      case 'REJECTED': return 'text-red-700 bg-red-100';
      case 'rejected': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'INTERNAL_VERIFIED': return 'Verified';
      case 'submitted': return 'Pending Review';
      case 'pending_review': return 'Pending Review';
      case 'REJECTED': return 'Rejected';
      case 'draft': return 'Draft';
      default: return (status || 'unknown').replace(/_/g, ' ');
    }
  };

  const handleReviewEvidence = (evidence: any) => {
    setSelectedEvidence(evidence);
  };

  const handleApprove = async () => {
    if (!selectedEvidence) return;

    try {
      await complianceExecutionApi.approveEvidence(selectedEvidence.id, assessmentNotes);
      setEvidence(prev => prev.map(item =>
        item.id === selectedEvidence.id
          ? { ...item, status: 'INTERNAL_VERIFIED' as const }
          : item
      ));
      setSelectedEvidence(null);
      setAssessmentNotes('');
      toast.success('Evidence approved successfully!');
    } catch (error) {
      console.error('Error approving evidence:', error);
      toast.error('Failed to approve evidence. Please try again.');
    }
  };

  const handleDownloadEvidence = async (evidenceId: number, fileName: string) => {
    try {
      const blob = await complianceExecutionApi.downloadEvidence(evidenceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading evidence:', error);
      toast.error('Failed to download evidence file');
    }
  };

  const confirmReject = async () => {
    if (!selectedEvidence) return;

    try {
      await complianceExecutionApi.rejectEvidence(selectedEvidence.id, rejectionReason);
      setEvidence(prev => prev.map(item =>
        item.id === selectedEvidence.id
          ? { ...item, status: 'REJECTED' as const }
          : item
      ));
      setSelectedEvidence(null);
      setAssessmentNotes('');
      setRejectionReason('');
      setShowRejectionModal(false);
      toast.success('Evidence rejected successfully!');
    } catch (error) {
      console.error('Error rejecting evidence:', error);
      toast.error('Failed to reject evidence. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-600 font-medium text-sm">Loading evidence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Evidence Review</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Review and validate evidence submitted for compliance requirements</p>
          </div>
          <button
            onClick={loadEvidence}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 mb-5">
          {([
            { key: 'all', label: 'All', count: evidence.length },
            { key: 'pending', label: 'Pending Review', count: evidence.filter(e => e.status === 'submitted' || e.status === 'pending_review').length },
            { key: 'approved', label: 'Approved', count: evidence.filter(e => e.status === 'INTERNAL_VERIFIED').length },
            { key: 'rejected', label: 'Rejected', count: evidence.filter(e => e.status === 'REJECTED').length },
          ] as { key: typeof activeTab; label: string; count: number }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
          {/* Inline next-step link — shows only when all pending have been decided.
              Nudges the auditor to the page-level review without adding a full banner. */}
          {(() => {
            const pendingCount = evidence.filter(e => e.status === 'submitted' || e.status === 'pending_review').length;
            const hasAnyDecision = evidence.some(e => e.status === 'INTERNAL_VERIFIED' || e.status === 'REJECTED');
            if (!loading && evidence.length > 0 && pendingCount === 0 && hasAnyDecision) {
              return (
                <Link
                  to="/audit/pages-review"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors ml-auto"
                  title="All evidence decided — next step is page-level approval"
                >
                  Continue to Pending Page Review <ArrowRight size={13} />
                </Link>
              );
            }
            return null;
          })()}
        </div>

        {/* Evidence List */}
        <div className="space-y-4 mb-6">
          {evidence
            .filter(item => {
              if (activeTab === 'all') return true;
              if (activeTab === 'pending') return item.status === 'submitted' || item.status === 'pending_review';
              if (activeTab === 'approved') return item.status === 'INTERNAL_VERIFIED';
              if (activeTab === 'rejected') return item.status === 'REJECTED';
              return true;
            })
            .map((item) => (
            <div key={item.id} className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">{item.requirement_title}</h3>
                    <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getStatusColor(item.status)}`}>
                      {getStatusLabel(item.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500">Company</p>
                      <p className="text-sm font-medium text-slate-900">{item.company_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Submitted By</p>
                      <p className="text-sm font-medium text-slate-900">{item.submitted_by_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Date Submitted</p>
                      <p className="text-sm font-medium text-slate-900">{new Date(item.submitted_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 mb-4">{item.description}</p>

                  {item.file_name && (
                    <div className="flex items-center gap-2 mb-4">
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="text-sm text-slate-600">{item.file_name}</span>
                      {/* OCR confidence is a subtle status tooltip, not a prominent red badge */}
                      {item.ocr_confidence_score !== undefined && item.ocr_confidence_score > 0 && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            item.ocr_confidence_score >= 0.8 ? 'text-emerald-700 bg-emerald-50' :
                            item.ocr_confidence_score >= 0.6 ? 'text-slate-600 bg-slate-100' :
                            'text-slate-500 bg-slate-50'
                          }`}
                          title={`OCR searchable text extraction confidence: ${Math.round(item.ocr_confidence_score * 100)}%. Does not affect audit decisions.`}
                        >
                          OCR {Math.round(item.ocr_confidence_score * 100)}%
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    {/* Single action per row. Decisions (Pass / Fail) happen inside
                        the modal where the auditor has actually seen the file and
                        can add Assessment Notes. Prevents quick approvals without
                        context. Matches Drata / Vanta / OneTrust pattern. */}
                    {(item.status === 'pending_review' || item.status === 'submitted') ? (
                      <button
                        onClick={() => handleReviewEvidence(item)}
                        className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        title="Open the evidence, review the file, and decide Pass or Fail with assessment notes"
                      >
                        Review Evidence
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReviewEvidence(item)}
                        className="px-4 py-2.5 bg-white text-slate-700 border border-slate-200 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                        title={`View the decision and audit trail for this ${item.status === 'INTERNAL_VERIFIED' ? 'verified' : 'rejected'} evidence`}
                      >
                        View Decision
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {evidence.length === 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Search size={20} className="text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No Evidence to Review</h3>
            <p className="text-sm text-slate-500">There is currently no evidence awaiting review.</p>
          </div>
        )}

        {/* Evidence Review Modal */}
        {selectedEvidence && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl border border-slate-100 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-slate-900">Review Evidence</h2>
                  <button
                    onClick={() => setSelectedEvidence(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-900">{selectedEvidence.requirement_title}</h3>
                      {/* Current status pill — so the auditor knows immediately whether the item was
                          already decided, or is still awaiting review */}
                      <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getStatusColor(selectedEvidence.status)}`}>
                        {getStatusLabel(selectedEvidence.status)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{selectedEvidence.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Company</p>
                      <p className="text-sm font-medium text-slate-900">{selectedEvidence.company_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Submitted By</p>
                      <p className="text-sm font-medium text-slate-900">{selectedEvidence.submitted_by_name}</p>
                    </div>
                  </div>

                  {selectedEvidence.file_name && (
                    <div
                      className="border border-slate-200 rounded-lg p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => handleDownloadEvidence(selectedEvidence.id, selectedEvidence.file_name!)}
                    >
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{selectedEvidence.file_name}</p>
                          <p className="text-xs text-blue-600">Click to download and review</p>
                        </div>
                        <Download size={16} className="text-slate-400" />
                      </div>
                    </div>
                  )}

                  {(() => {
                    const pending = selectedEvidence.status === 'submitted' || selectedEvidence.status === 'pending_review';
                    if (pending) {
                      return (
                        <>
                          <div className="border-t border-slate-200 pt-6">
                            <h4 className="text-sm font-semibold text-slate-900 mb-3">Assessment Notes</h4>
                            <textarea
                              value={assessmentNotes}
                              onChange={(e) => setAssessmentNotes(e.target.value)}
                              placeholder="Enter your assessment notes..."
                              className="w-full h-32 p-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={handleApprove}
                              className="px-4 py-2.5 bg-green-600 text-white font-medium text-sm rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Pass
                            </button>
                            <button
                              onClick={() => {
                                setRejectionReason('');
                                setShowRejectionModal(true);
                              }}
                              className="px-4 py-2.5 bg-red-600 text-white font-medium text-sm rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Fail
                            </button>
                            <button
                              onClick={() => {
                                setSelectedEvidence(null);
                                setAssessmentNotes('');
                              }}
                              className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      );
                    }
                    // Already decided — read-only view with close button
                    const isVerified = selectedEvidence.status === 'INTERNAL_VERIFIED';
                    const reviewerIsCurrentUser = !!(selectedEvidence.reviewed_by && user?.id && selectedEvidence.reviewed_by === user.id);
                    const reviewerDisplay = reviewerIsCurrentUser
                      ? 'you'
                      : (selectedEvidence.reviewer_name && selectedEvidence.reviewer_name.trim()) || null;
                    const reviewedWhen = selectedEvidence.reviewed_at
                      ? new Date(selectedEvidence.reviewed_at).toLocaleString()
                      : null;
                    const actionWord = isVerified ? 'verified' : 'rejected';
                    const attribution = reviewerDisplay
                      ? `${actionWord.charAt(0).toUpperCase() + actionWord.slice(1)} by ${reviewerDisplay}${reviewedWhen ? ` on ${reviewedWhen}` : ''}`
                      : `This evidence has been ${actionWord}`;
                    const guidance = isVerified
                      ? 'The decision is final. To change it, use the page-level "Request Changes" action in Pending Page Review — that will send the whole page back to the compliance user for re-work.'
                      : 'The compliance user should re-upload a replacement file before the page can move forward.';
                    return (
                      <>
                        <div className={`border-t border-slate-200 pt-6 ${isVerified ? 'bg-emerald-50' : 'bg-red-50'} -mx-6 px-6 py-4`}>
                          <p className={`text-sm font-semibold ${isVerified ? 'text-emerald-800' : 'text-red-800'}`}>
                            {isVerified ? '✓ ' : '✗ '}{attribution}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">{guidance}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              setSelectedEvidence(null);
                              setAssessmentNotes('');
                            }}
                            className="px-4 py-2.5 bg-slate-700 text-white font-medium text-sm rounded-lg hover:bg-slate-800 transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Confirmation Modal */}
        {showRejectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl border border-slate-100 max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-slate-900">Confirm Rejection</h2>
                  <button
                    onClick={() => setShowRejectionModal(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Are you sure you want to reject this evidence? This action cannot be undone.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Reason for Rejection <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please provide a detailed reason for rejection..."
                      className="w-full h-24 p-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={confirmReject}
                      disabled={!rejectionReason.trim()}
                      className="px-4 py-2.5 bg-red-600 text-white font-medium text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reject Evidence
                    </button>
                    <button
                      onClick={() => setShowRejectionModal(false)}
                      className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidenceReview;
